import { Injectable } from '@angular/core';
import { ChatMessage } from '../models/chat.models';
import { TimezoneService } from 'src/app/core/utils/timezone.service';
import { DateFormatLocalizationService } from 'src/app/core/services/date-format-localization.service';

@Injectable({ providedIn: 'root' })
export class ChatExportService {
  constructor(
    private readonly timezoneService: TimezoneService,
    private readonly dateFormatLocalization: DateFormatLocalizationService,
  ) {}

  /**
   * Exports a conversation as a downloadable PDF.
   * Uses html2canvas + jsPDF (already in package.json).
   */
  async exportToPdf(messages: ReadonlyArray<ChatMessage>, title: string): Promise<void> {
    const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
      import('jspdf'),
      import('html2canvas'),
    ]);

    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    const contentWidth = pageWidth - margin * 2;
    let y = margin;

    // Title
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(title, margin, y);
    y += 8;

    // Date
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(120, 120, 120);
    const country = this.dateFormatLocalization.getCurrentCountryCode();
    doc.text(`Exportado: ${this.timezoneService.formatDateForCenter(new Date(), country)}`, margin, y);
    y += 8;

    doc.setTextColor(0, 0, 0);
    doc.setDrawColor(200, 200, 200);
    doc.line(margin, y, pageWidth - margin, y);
    y += 6;

    // Messages
    for (const msg of messages) {
      if (!msg.content || msg.content.trim().length === 0) continue;

      // Check page break
      if (y > 270) {
        doc.addPage();
        y = margin;
      }

      // Role label
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(msg.role === 'user' ? 26 : 0, msg.role === 'user' ? 26 : 196, msg.role === 'user' ? 46 : 140);
      const roleLabel = msg.role === 'user' ? 'Tú' : msg.role === 'assistant' ? 'Copilot' : 'Sistema';
      const time = this.formatTime(msg.createdAt);
      doc.text(`${roleLabel}  ${time}`, margin, y);
      y += 4;

      // Content — strip HTML tags and fenced blocks for PDF
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(30, 30, 30);
      const cleanText = this.stripHtmlAndBlocks(msg.content);
      const lines = doc.splitTextToSize(cleanText, contentWidth);
      for (const line of lines) {
        if (y > 275) {
          doc.addPage();
          y = margin;
        }
        doc.text(line, margin, y);
        y += 4.5;
      }
      y += 4;
    }

    // Footer
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(`Cafler Copilot — Página ${i}/${pageCount}`, margin, 290);
    }

    const fileName = `copilot-${title.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}-${Date.now()}.pdf`;
    doc.save(fileName);
  }

  private formatTime(iso: string): string {
    try {
      const d = new Date(iso);
      return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
    } catch {
      return '';
    }
  }

  private stripHtmlAndBlocks(text: string): string {
    let clean = text;

    // Expand ```report blocks into readable text tables
    clean = clean.replace(/```report\s*\n([\s\S]*?)\n```/g, (_match, json) => {
      try {
        const spec = JSON.parse(json) as { title?: string; subtitle?: string; columns?: string[]; rows?: string[][]; summary?: string };
        const lines: string[] = [];
        if (spec.title) lines.push(`\n── ${spec.title} ──`);
        if (spec.subtitle) lines.push(spec.subtitle);
        if (spec.columns?.length) {
          lines.push(spec.columns.join(' | '));
          lines.push(spec.columns.map(() => '─────').join('─┼─'));
        }
        for (const row of spec.rows ?? []) lines.push(row.join(' | '));
        if (spec.summary) lines.push(`\n${spec.summary}`);
        return lines.join('\n');
      } catch { return ''; }
    });

    // Expand ```chart blocks into a compact data summary
    clean = clean.replace(/```chart\s*\n([\s\S]*?)\n```/g, (_match, json) => {
      try {
        const spec = JSON.parse(json) as { title?: string; series?: Array<{ name: string; data: Array<{ x: string; y: number }> }> };
        const lines: string[] = [];
        if (spec.title) lines.push(`\n── ${spec.title} ──`);
        for (const s of spec.series ?? []) {
          lines.push(s.name + ': ' + s.data.map((d: { x: string; y: number }) => `${d.x}: ${d.y}`).join(', '));
        }
        return lines.join('\n');
      } catch { return ''; }
    });

    // Remove remaining fenced blocks (actions, suggestions, title, product_card, onboarding_guide)
    clean = clean.replace(/```(?:actions|suggestions|title|product_card|onboarding_guide)\s*\n[\s\S]*?\n```/g, '');
    // Remove HTML tags
    clean = clean.replace(/<[^>]+>/g, '');
    // Collapse whitespace
    clean = clean.replace(/\n{3,}/g, '\n\n').trim();
    return clean;
  }
}
